// Reading PostgREST RPC failures. Kept framework-free (and out of the hooks) so
// the branching is unit-testable — the hooks just render what these return.
//
// The throttle on `household_id_for_code` (017_rate_limiting.sql) raises
// SQLSTATE 'PT429'. PostgREST maps a PTxyz code to HTTP status xyz, so a
// throttled call arrives as a real 429 *and* carries `code: "PT429"` in its
// error body. We test those two structured signals rather than substring-
// matching the message, which would silently stop working the first time
// someone rewords the SQL.

/** SQLSTATE raised by the invite-code throttle; PostgREST turns it into a 429. */
export const RATE_LIMIT_SQLSTATE = 'PT429'

/** Single user-facing string, so the call sites can't drift apart. */
export const RATE_LIMITED_MESSAGE = 'Too many attempts — try again in a few minutes'

/** SQLSTATE the seat guard raises for a join into a full household (022, 031); a 402 over HTTP. */
export const HOUSEHOLD_FULL_SQLSTATE = 'PT402'

export const HOUSEHOLD_FULL_MESSAGE = 'This household is full — it can have up to 6 people'

/** SQLSTATE for the last member of a paying household trying to leave (022); a 409. */
export const LAST_MEMBER_SQLSTATE = 'PT409'

export const LAST_MEMBER_MESSAGE = 'Cancel the subscription before leaving — nobody else could'

/**
 * A PostgREST error as seen from either call style: the parsed JSON body of a
 * raw `fetch` (which may be null if the response had no body), or the
 * `PostgrestError` that supabase-js returns. `status` is only available on the
 * raw-fetch path.
 */
export type RpcFailure = {
  code?: unknown
  message?: unknown
} | null | undefined

/** SQLSTATE for trying to remove the payer of an active subscription (026); a 423. */
export const PAYER_LOCKED_SQLSTATE = 'PT423'

export const PAYER_LOCKED_MESSAGE = "The person who pays can't be removed while the subscription is active — cancel it first"

/**
 * One rejection, recognised two ways: the HTTP status PostgREST derived from the
 * SQLSTATE, or the SQLSTATE itself in the body. The status alone counts, because
 * a body that failed to parse still leaves the status trustworthy. The `typeof`
 * guard is for the compiler, not the comparison — `code` is `unknown`.
 */
function raised(body: RpcFailure, status: number | undefined, httpStatus: number, sqlstate: string): boolean {
  if (status === httpStatus) return true
  return typeof body?.code === 'string' && body.code === sqlstate
}

/**
 * True when an RPC failure is the rate-limit throttle rather than an ordinary
 * rejection (a bad invite code, a network blip). Pass the HTTP status when the
 * caller has one.
 */
export function isRateLimited(body: RpcFailure, status?: number): boolean {
  return raised(body, status, 429, RATE_LIMIT_SQLSTATE)
}

/** True when a join or move was refused because the household already has six people. */
export function isHouseholdFull(body: RpcFailure, status?: number): boolean {
  return raised(body, status, 402, HOUSEHOLD_FULL_SQLSTATE)
}

/** True when leaving was refused because the caller is the last member of a paying household. */
export function isLastMember(body: RpcFailure, status?: number): boolean {
  return raised(body, status, 409, LAST_MEMBER_SQLSTATE)
}

/** True when a removal was refused because the target is the household's payer. */
export function isPayerLocked(body: RpcFailure, status?: number): boolean {
  return raised(body, status, 423, PAYER_LOCKED_SQLSTATE)
}
